import * as React from 'react'
import {
  matchPath as reactRouterMatchPath,
  useLocation,
  useNavigate as useUnlocalizedNavigate,
} from 'react-router'
import {
  getStudioLanguageFromPathname,
  stripStudioLocalePrefix,
  stripStudioLocalePrefixFromPathname,
} from '@repo/react-router-locale/canonicalize-studio-locale-pathname'
import { canonicalizeStudioLocaleTo } from '@repo/react-router-locale/canonicalize-studio-locale-to'
import { Link, useNavigate } from '@repo/react-router-locale/locale-router'
import { StudioLayoutContext, useStudioConfig } from './studio-config-context'
import type {
  MatchPathFn,
  MatchPathPattern,
  NavigateToFn,
  StudioLayoutLinkComponent,
  StudioLayoutLinkProps,
} from './studio-config-context'

// --- Routing adapter: default react-router implementations -------------------
// Importing this module pulls in react-router, so it is the Vite-only half of
// the config package. Next.js hosts stay on `./studio-config-context` and inject
// their own `next/navigation`-backed adapters on `StudioConfig`.

function useReactRouterRawPathname(): string {
  return useLocation().pathname
}

function useReactRouterLocationState(): unknown {
  return useLocation().state
}

function useReactRouterRawMaskPathname(): string | undefined {
  return useLocation().mask?.pathname
}

function useNoMaskPathname(): undefined {
  return undefined
}

function assignDocument(href: string, replace?: boolean) {
  if (replace) {
    window.location.replace(href)
    return
  }

  window.location.assign(href)
}

function useReactRouterNavigateTo(): NavigateToFn {
  // `useNavigate` reapplies the locale from the URL, same as `Link`.
  const navigate = useNavigate()

  return React.useCallback<NavigateToFn>(
    (path, options) => {
      if (options?.reloadDocument) {
        const locale = getStudioLanguageFromPathname(window.location.pathname)
        assignDocument(locale ? canonicalizeStudioLocaleTo(path, locale) : path, options.replace)
        return
      }

      navigate(path, {
        mask: options?.mask,
        state: options?.state,
        replace: options?.replace,
      })
    },
    [navigate],
  )
}

function useReactRouterNavigateToExact(): NavigateToFn {
  const navigate = useUnlocalizedNavigate()

  return React.useCallback<NavigateToFn>(
    (path, options) => {
      if (options?.reloadDocument) {
        assignDocument(path, options.replace)
        return
      }

      navigate(path, {
        mask: options?.mask,
        state: options?.state,
        replace: options?.replace,
      })
    },
    [navigate],
  )
}

export function matchStudioPath(pattern: MatchPathPattern, pathname: string): boolean {
  return Boolean(reactRouterMatchPath(pattern, stripStudioLocalePrefixFromPathname(pathname)))
}

// --- Routing adapter: resolver hooks ----------------------------------------
// These pick the injected implementation when present, otherwise fall back to
// react-router. The chosen implementation is stable for the lifetime of the
// app (a single app never swaps routers), so the rules-of-hooks contract holds.

export function useStudioRawPathname(): string {
  const impl = useStudioConfig()?.useCurrentPathname ?? useReactRouterRawPathname
  return impl()
}

export function useStudioRawMaskPathname(): string | undefined {
  const config = useStudioConfig()
  const impl =
    config?.useCurrentMaskPathname ??
    (config?.useCurrentPathname ? useNoMaskPathname : useReactRouterRawMaskPathname)
  return impl()
}

export function useStudioPathname(): string {
  return stripStudioLocalePrefix(useStudioRawPathname())
}

export function useStudioLocationState(): unknown {
  const impl = useStudioConfig()?.useCurrentLocationState ?? useReactRouterLocationState
  return impl()
}

export function useStudioNavigateTo(): NavigateToFn {
  const impl = useStudioConfig()?.useNavigateTo ?? useReactRouterNavigateTo
  return impl()
}

/**
 * Navigates to a path exactly as given, with no locale reapplied.
 *
 * For callers that have already resolved the locale themselves. Sending such a
 * path through `useStudioNavigateTo` re-derives the prefix from the URL the
 * caller is trying to leave, and the two disagree in exactly one direction:
 * moving a `/fr` reader to English yields `/`, which is then read back as
 * "unprefixed, so inherit `fr`" and rewritten to `/fr`. The URL never followed
 * the picker even though the content and the picker did.
 *
 * Only the react-router default differs between the two hooks; adapters injected
 * by the Next.js apps never localized, so they serve both.
 */
export function useStudioNavigateToExact(): NavigateToFn {
  const impl = useStudioConfig()?.useNavigateTo ?? useReactRouterNavigateToExact
  return impl()
}

export function useStudioMatchPath(): MatchPathFn {
  return useStudioConfig()?.matchPath ?? matchStudioPath
}

function DefaultStudioLayoutLink({ mask: _mask, ...props }: StudioLayoutLinkProps) {
  return <Link {...props} />
}

export function useStudioLayoutLink(): StudioLayoutLinkComponent {
  const layoutContext = React.useContext(StudioLayoutContext)
  const studioConfig = useStudioConfig()

  return layoutContext.LinkComponent ?? studioConfig?.LinkComponent ?? DefaultStudioLayoutLink
}

/**
 * Renders a link using the LinkComponent from StudioLayoutContext.
 * Defaults to the locale-aware Link for SPA navigation, so `ROUTES` targets keep
 * the prefix; pass a custom LinkComponent to StudioLayout to use hard navigations
 * (e.g. plain <a>) or a framework's own link, as the Next.js apps do.
 */
export function StudioLayoutLink({ to, ...props }: StudioLayoutLinkProps) {
  const LinkComponent = useStudioLayoutLink()
  return <LinkComponent to={to} {...props} />
}
